<template>
    <div>
        <common-notification
            ref="commonSuccessNotificationChild"
            :content="$t('pages.referrals.show.details.request_sent')"
        ></common-notification>

        <div class="referral-tracking__referral-tabs">
            <button type="button" :class="getReferralTabClass('received')" @click="toggleReferralActiveTab('received')">
                <span class="referral-tracking__tab-icon">
                    <common-icon name="arrow-up-right" size="semi-small"></common-icon>
                </span>
                <span class="referral-tracking__tab-label">
                    {{ $t('pages.referrals.index.referrals_made.received_referral') }}
                </span>
                <span :class="getReferralCountClass(receivedPendingCount)">
                    {{ receivedPendingCount || '' }}
                </span>
            </button>
            <button type="button" :class="getReferralTabClass('made')" @click="toggleReferralActiveTab('made')">
                <span class="referral-tracking__tab-icon">
                    <common-icon name="arrow-down-left" size="semi-small"></common-icon>
                </span>
                <span class="referral-tracking__tab-label">
                    {{ $t('pages.referrals.index.referrals_made.received_made') }}
                </span>
                <span :class="getReferralCountClass(madePendingCount)">
                    {{ madePendingCount || '' }}
                </span>
            </button>
        </div>

        <div v-if="activeReferralTab === ReferralPathTypeEnum.RECEIVED">
            <div class="referral-tracking__referrals-made-card">
                <div class="referral-tracking__referrals-filter-row">
                    <div class="filter-left">
                        <span class="filter-icon">
                            <common-icon name="filter" size="small"></common-icon>
                        </span>
                        <span>{{ $t('pages.referrals.index.referrals_made.filters') }}</span>
                        <span class="ref-count">
                            {{ $t('pages.referrals.index.referrals_made.referrals_filters', { n: referralsTotalCount }) }}
                        </span>
                    </div>
                    <!-- <div class="print-icon">
                        <common-icon name="print" size="small"></common-icon>
                    </div> -->
                </div>
                <table class="referral-tracking__referrals-made-table">
                    <thead>
                        <tr>
                            <th>{{ $t('pages.referrals.index.referrals_made.id_received') }}</th>
                            <th v-if="!isTablet">{{ $t('pages.referrals.index.referrals_made.patient_name') }}</th>
                            <th v-if="!isMobile">{{ $t('pages.referrals.index.referrals_made.stage') }}</th>
                            <th v-if="!isTablet">{{ $t('pages.referrals.index.headings.status') }}</th>
                            <th v-if="!isTablet">{{ $t('pages.referrals.index.referrals_made.timing') }}</th>
                            <th v-if="!isTablet">{{ $t('pages.referrals.index.referrals_made.typer') }}</th>
                            <th>{{ $t('pages.referrals.index.referrals_made.action') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="referral in referrals">
                            <td class="referral-tracking__card-referral-layout">
                                <div class="user-cell">
                                    <div class="user-avatar">
                                        <common-user-avatar
                                            size=""
                                            :user="referral?.fromUser || referral?.fromPractice"
                                        ></common-user-avatar>
                                    </div>
                                    <div>
                                        <p class="name">{{ getUserName(referral) }}</p>
                                        <p class="sub">{{ getUserSpeciality(referral?.fromUser) }}</p>
                                        <p class="date">{{ getCreatedDate(referral) }}</p>
                                    </div>
                                </div>
                            </td>
                            <td v-if="!isTablet">
                                <p class="referral-tracking__name">{{ getUserFullName(referral.patient) }}</p>
                                <p class="sub">
                                    {{ $t('pages.referrals.index.referrals_made.dob') }}
                                    {{ getPatientDateOfBirth(referral.patient.dateOfBirth) }}
                                </p>
                                <p class="sub">
                                    <span class="label">{{ $t('forms.practice_ancillary.fields.insurance') }}:</span>
                                    <span class="referral-tracking__name">
                                        {{ referral?.insurancePlan?.name }}
                                    </span>
                                </p>
                            </td>
                            <td v-if="!isMobile">
                                <div class="referral-tracking__stage">
                                    <span :class="getStageClasses(referral)" @click="handleStageAction(referral)">
                                        {{ formatStage(referral?.stageInfo?.stage) }}
                                    </span>

                                    <div class="referral-tracking__stage-tooltip-wrapper">
                                        <common-icon
                                            name="info"
                                            size="semi-small"
                                            class="referral-tracking__stage-info-icon"
                                        ></common-icon>

                                        <div class="referral-tracking__stage-tooltip">
                                            {{ getStageTooltip(referral) }}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td v-if="!isTablet">
                                <div class="referral-tracking__status-cell">
                                    <span :class="getStatusBadgeClass(referral)">
                                        <span class="referral-tracking__status-content">
                                            <span>{{ getStatusText(referral) }}</span>
                                            <span>{{ scheduledForDate(referral.appointmentDateTime) }}</span>
                                        </span>
                                    </span>

                                    <div
                                        v-if="isStatusChangeable(referral)"
                                        v-clickaway="closeStatusDropdown"
                                        class="referral-context-menu"
                                    >
                                        <span class="dots" @click.stop="handleStatusClick(referral)">⋮</span>

                                        <div
                                            v-if="activeStatusDropdown === referral.id"
                                            class="referral-context-menu__dropdown-menus referral-actions"
                                        >
                                            <span
                                                v-for="option in dropdownOptions(referral)"
                                                :key="option"
                                                class="referral-context-menu__menu-item referral-context-menu__with-status"
                                                @click="handleStatusChange(referral, option)"
                                            >
                                                <span>
                                                    {{
                                                        $t(
                                                            `pages.referrals.tracking.status_title.${getDisplayStatus(
                                                                option,
                                                                referral.sourceType,
                                                            )}`,
                                                        )
                                                    }}
                                                </span>
                                                <span
                                                    class="referral-context-menu__status-dot"
                                                    :class="getDropdownStatusColor(option)"
                                                ></span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td v-if="!isTablet">
                                <span :class="getTimingClass(referral?.stageInfo)">
                                    {{ getTimingText(referral) }}
                                </span>
                            </td>
                            <td v-if="!isTablet">
                                <span class="referral-tracking__referrals" :class="referralTypeClass(referral?.sourceType)">
                                    {{ getReferralType(referral?.sourceType) }}
                                </span>
                            </td>
                            <td class="referral-tracking__view-port">
                                <div class="referral-tracking__confirt-detail">
                                    <button
                                        v-if="!isTablet"
                                        class="referral-tracking__view-details-btn"
                                        @click="openDetailsModal(referral.id, referral.sourceType)"
                                    >
                                        {{ $t('pages.referrals.index.referrals_made.detail') }}
                                    </button>
                                    <div>
                                        <referral-details-context-menu
                                            :referral="referral"
                                            :type="activeReferralTab"
                                            :isDisabled="false"
                                            @select-referral="selectedReferralForActions = $event"
                                            @open-details="openDetailsModal(referral.id, referral.sourceType)"
                                            @propose-slot="(data) => goToProposeTime(data, activeReferralTab)"
                                            @request-additional-records="
                                                openDetailsModal(referral.id, referral.sourceType, 'records')
                                            "
                                            @reschedule="openRescheduleModal(referral)"
                                            @follow-up-visit="openFollowUpVisitModal(referral)"
                                            @cancel="openCancellationModal"
                                            @match="matchReferral(referral)"
                                            @patientSeen="openPatientSeenModal(referral)"
                                            @addReport="addNotesClicked(referral)"
                                            @downloadPdf="downloadPdf(referral)"
                                            @requestReport="openRequestReportModal(referral)"
                                            @external-decline="declineReferral(referral)"
                                        ></referral-details-context-menu>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-if="activeReferralTab === ReferralPathTypeEnum.MADE">
            <div class="referral-tracking__referrals-made-card">
                <div class="referral-tracking__referrals-filter-row">
                    <div class="filter-left">
                        <span class="filter-icon">
                            <common-icon name="filter" size="small"></common-icon>
                        </span>
                        <span>{{ $t('pages.referrals.index.referrals_made.filters') }}</span>
                        <span class="ref-count">
                            {{ $t('pages.referrals.index.referrals_made.track_sub_title', { n: referralsTotalCount }) }}
                        </span>
                    </div>
                    <!-- <div class="print-icon">
                        <common-icon name="print" size="small"></common-icon>
                    </div> -->
                </div>

                <table class="referral-tracking__referrals-made-table">
                    <thead>
                        <tr>
                            <th>{{ $t('pages.referrals.index.referrals_made.id_made') }}</th>
                            <th v-if="!isTablet">{{ $t('pages.referrals.index.referrals_made.patient_name') }}</th>
                            <th v-if="!isMobileLarge">{{ $t('pages.referrals.index.referrals_made.stage') }}</th>
                            <th v-if="!isTablet">{{ $t('pages.referrals.index.headings.status') }}</th>
                            <th v-if="!isTablet">{{ $t('pages.referrals.index.referrals_made.timing') }}</th>
                            <th>{{ $t('pages.referrals.index.referrals_made.action') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="referral in referrals">
                            <td class="referral-tracking__card-referral-layout">
                                <div v-if="referral?.sourceType === ReferralTrackingTypeEnum.DIRECT" class="user-cell">
                                    <div class="user-avatar">
                                        <common-user-avatar
                                            size=""
                                            :user="referral?.toUser || referral?.practice"
                                        ></common-user-avatar>
                                    </div>
                                    <div>
                                        <p class="name">{{ getUserName(referral) }}</p>
                                        <p class="sub">{{ getUserSpeciality(referral?.toUser) }}</p>
                                        <p class="date">{{ getCreatedDate(referral) }}</p>
                                    </div>
                                </div>
                                <span v-else class="user-cell">
                                    <button :class="getReferralAvatarClass(referral)">
                                        <common-icon :name="getReferralMadeMatchIcon(referral)" size="small"></common-icon>
                                    </button>
                                    <div>
                                        <div v-if="hasAdditionalTests(referral)" class="referral-tracking__stage-tooltip-wrapper">
                                            <div class="test-name">
                                                <p class="name">
                                                    {{ getMatchReferralTitle(referral) }}
                                                    <span class="date additional-count">
                                                        +{{ getAdditionalTestsCount(referral) }}
                                                    </span>
                                                </p>
                                            </div>

                                            <div class="referral-tracking__stage-tooltip">
                                                {{ getAncillaryTestsTooltip(referral) }}
                                            </div>
                                        </div>

                                        <p v-else class="name">
                                            {{ getMatchReferralTitle(referral) }}
                                        </p>
                                        <p class="timeframe">
                                            <span
                                                class="referral-tracking__referrals"
                                                :class="referralTypeClass(referral?.sourceType)"
                                            >
                                                {{ getReferralType(referral?.sourceType) }}
                                            </span>
                                            - {{ getTimeframeLabel(referral) }}
                                        </p>
                                        <p class="date">{{ getCreatedDate(referral) }}</p>
                                        <button class="view-provider-btn" @click="openProvidersModal(referral.id)">
                                            <common-icon name="user" size="tiny"></common-icon>
                                            <template v-if="isMobileLarge">
                                                {{ getProviderCount(referral) }}
                                            </template>
                                            <template v-else>
                                                <span v-if="!isMobileLarge" class="view-provider-content">
                                                    {{ getProviderLabel(referral) }}
                                                </span>

                                                <common-icon v-if="!isMobileLarge" name="arrow-right" size="tiny"></common-icon>
                                            </template>
                                        </button>
                                    </div>
                                </span>
                            </td>
                            <td v-if="!isTablet">
                                <p class="referral-tracking__name">{{ getUserFullName(referral.patient) }}</p>
                                <p class="sub">
                                    {{ $t('pages.referrals.index.referrals_made.dob') }}
                                    {{ getPatientDateOfBirth(referral.patient.dateOfBirth) }}
                                </p>
                                <p class="sub">
                                    <span class="label">{{ $t('forms.practice_ancillary.fields.insurance') }}:</span>
                                    <span class="referral-tracking__name">
                                        {{ referral?.insurancePlan?.name }}
                                    </span>
                                </p>
                            </td>
                            <td v-if="!isMobileLarge">
                                <div class="referral-tracking__stage">
                                    <span :class="getStageClasses(referral)" @click="handleStageAction(referral)">
                                        {{ formatStage(referral?.stageInfo?.stage) }}
                                    </span>

                                    <div class="referral-tracking__stage-tooltip-wrapper">
                                        <common-icon
                                            name="info"
                                            size="semi-small"
                                            class="referral-tracking__stage-info-icon"
                                        ></common-icon>

                                        <div class="referral-tracking__stage-tooltip">
                                            {{ getStageTooltip(referral) }}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td v-if="!isTablet">
                                <div class="referral-tracking__status-cell">
                                    <span :class="getStatusBadgeClass(referral)">
                                        <span class="referral-tracking__status-content">
                                            <span>{{ getStatusText(referral) }}</span>
                                            <span>{{ scheduledForDate(referral.appointmentDateTime) }}</span>
                                        </span>
                                    </span>

                                    <div
                                        v-if="isStatusChangeable(referral)"
                                        v-clickaway="closeStatusDropdown"
                                        class="referral-context-menu"
                                    >
                                        <span class="dots" @click.stop="handleStatusClick(referral)">⋮</span>

                                        <div
                                            v-if="activeStatusDropdown === referral.id"
                                            class="referral-context-menu__dropdown-menus referral-actions"
                                        >
                                            <span
                                                v-for="option in dropdownOptions(referral)"
                                                :key="option"
                                                class="referral-context-menu__menu-item referral-context-menu__with-status"
                                                @click="handleStatusChange(referral, option)"
                                            >
                                                <span>
                                                    {{
                                                        $t(
                                                            `pages.referrals.tracking.status_title.${getDisplayStatus(
                                                                option,
                                                                referral.sourceType,
                                                            )}`,
                                                        )
                                                    }}
                                                </span>
                                                <span
                                                    class="referral-context-menu__status-dot"
                                                    :class="getDropdownStatusColor(option)"
                                                ></span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td v-if="!isTablet">
                                <span :class="getTimingClass(referral?.stageInfo)">
                                    {{ getTimingText(referral) }}
                                </span>
                            </td>
                            <td class="referral-tracking__view-port">
                                <div class="referral-tracking__confirt-detail">
                                    <button
                                        v-if="!isTablet"
                                        class="referral-tracking__view-details-btn"
                                        @click="openDetailsModal(referral.id, referral.sourceType)"
                                    >
                                        {{ $t('pages.referrals.index.referrals_made.detail') }}
                                    </button>
                                    <div>
                                        <referral-details-context-menu
                                            :referral="referral"
                                            :type="activeReferralTab"
                                            :isDisabled="false"
                                            @select-referral="selectedReferralForActions = $event"
                                            @open-details="openDetailsModal(referral.id, referral.sourceType)"
                                            @choose-slot="chooseSlotMethod"
                                            @reschedule="openRescheduleModal(referral)"
                                            @follow-up-visit="openFollowUpVisitModal(referral)"
                                            @cancel="openCancellationModal"
                                            @match="matchReferral(referral)"
                                            @patientSeen="openPatientSeenModal(referral)"
                                            @addReport="addNotesClicked(referral)"
                                            @downloadPdf="downloadPdf(referral)"
                                            @requestReport="openRequestReportModal(referral)"
                                        ></referral-details-context-menu>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <div>
            <referral-modal
                v-if="showViewDetailsModal"
                :showAsInnerModal="false"
                :referral="activeReferral"
                :type="activeReferralTab"
                :sourceType="activeSourceType"
                :isOpen="showViewDetailsModal"
                :isFetching="isFetching"
                @open-direct-referral="openDirectReferralFromMatchReferal"
                @close="closeDetailsModal"
                @fetch-referral="refreshMatchReferral"
            ></referral-modal>
            <referrals-pool-providers-modal
                v-if="showReferralsPoolProvidersModal"
                :referralPoolId="activeReferralPoolId"
                @close="closeProvidersModal"
            ></referrals-pool-providers-modal>
            <pending-reschedule-modal
                v-if="showRescheduleModal"
                :referral="selectedReferralForActions"
                :followUpReferral="followUpReferral"
                @deny="
                    closeRescheduleModal()
                    openCancellationModal()
                "
                @reschedule="handleRescheduleAppointment"
                @close="closeRescheduleModal"
            ></pending-reschedule-modal>
            <referral-cancellation-modal
                v-if="showCancellationModal"
                :referral="selectedReferralForActions"
                :type="isReferringPhysician ? 'referring' : 'referred'"
                :withSecondOption="!isReferringPhysician || isReferralError(selectedReferralForActions)"
                @close="closeCancellationModal"
                @confirm="cancelAppointment"
                @deny="cancellationModalDenyAction"
            ></referral-cancellation-modal>
            <referrals-is-patient-seen-modal
                v-if="showPatientSeenModal"
                :referral="selectedReferralForActions"
                @patient-seen="patientSeen"
                @skip-all="showPatientSeenModal = false"
                @close="showPatientSeenModal = false"
            ></referrals-is-patient-seen-modal>
            <common-modal
                v-if="isPrescriptionModalOpen"
                :isOpen="isPrescriptionModalOpen"
                width="large"
                @close="closePrescriptionModal"
            >
                <referral-prescription :referral="selectedReferralForActions"></referral-prescription>
            </common-modal>
            <request-report-modal
                v-if="showRequestReportModal"
                :isOpen="showRequestReportModal"
                :referral="selectedReferralForActions"
                @close="closeRequestReportModal"
                @sent="requestReportSent"
            ></request-report-modal>
            <is-report-uploaded-modal
                v-if="isReportUploadedModal"
                :isOpen="isReportUploadedModal"
                :referral="selectedReferralForActions"
                @refresh-details="openDetailsModal(selectedReferralForActions.id, selectedReferralForActions.sourceType)"
                @close="isReportUploadedModal = false"
            ></is-report-uploaded-modal>
        </div>
        <common-buttons class="referral-tracking__show-more">
            <common-button
                v-if="referrals.length < referralsTotalCount"
                :isProcessing="isProcessing"
                :label="$t('pages.referrals.index.buttons.show_more')"
                type="primary"
                size="large"
                @click="showMore()"
            ></common-button>
        </common-buttons>
    </div>
</template>
<script>
    import dayjs from 'dayjs'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import ReferralModal from './ReferralModal.vue'
    import ReferralsAPI from 'api/ReferralsAPI'
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
    import UrgencyEnum from '@/enums/UrgencyEnum'
    import StagesEnum from '@/enums/StagesEnum'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'
    import ReferralsPoolProvidersModal from 'components/page-elements/referrals/ReferralsPoolProvidersModal'
    import ReferralDetailsContextMenu from 'components/page-elements/ReferralDetailsContextMenu'
    import PendingRescheduleModal from 'components/page-elements/referrals-show-pending/PendingRescheduleModal'
    import ReferralCancellationModal from 'components/page-elements/ReferralCancellationModal'
    import ReferralRescheduleMixin from 'mixins/ReferralRescheduleMixin'
    import ReferralStatusMixin from 'mixins/ReferralStatusMixin'
    import ReferralPrescription from 'components/page-elements/referrals/ReferralPrescription'
    import RequestReportModal from 'components/page-elements/referrals/RequestConsultationReportModal'
    import Store from '~/store'
    import ReferralPathTypeEnum from '@/enums/ReferralPathTypeEnum'
    import ReferralTrackingTypeEnum from '@/enums/ReferralTrackingTypeEnum'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import IsReportUploadedModal from './IsReportUploadedModal.vue'
    import ReferralMatchMixin from 'mixins/ReferralMatchMixin'
    import ReferralPoolProposeNavigationMixin from 'mixins/ReferralPoolProposeNavigationMixin'
    import TimeWindowSearchCriteriaEnum from '@/enums/TimeWindowSearchCriteriaEnum'

    export default {
        mixins: [
            PageWithTitleMixin,
            PageWithElementsMixin,
            PageWithDataMixin,
            ReferralRescheduleMixin,
            ReferralStatusMixin,
            ScreenSizeDetectionMixin,
            ReferralMatchMixin,
            ReferralPoolProposeNavigationMixin,
        ],
        components: {
            ReferralModal,
            ReferralsPoolProvidersModal,
            ReferralDetailsContextMenu,
            PendingRescheduleModal,
            ReferralCancellationModal,
            ReferralPrescription,
            RequestReportModal,
            IsReportUploadedModal,
        },

        data() {
            return {
                ReferralPathTypeEnum,
                ReferralTrackingTypeEnum,
                isFetching: false,
                showViewDetailsModal: false,
                activeDropdown: null,
                activeMainTab: 'provider',
                activePatientTab: 'requests',
                showModal: false,
                referrals: [],
                showPatientDetailsModal: false,
                selectedReferralForActions: {},
                activeReferral: {},
                activeSourceType: null,
                PracticeTypeEnum: PracticeTypeEnum,
                practiceData: {},
                dropdownStates: {},
                actionStates: {},
                showReferralsPoolProvidersModal: false,
                activeReferralPoolId: null,
                referralsTotalCount: 0,
                limit: 10,
                offset: 0,
                isProcessing: false,
                showRescheduleModal: false,
                showCancellationModal: false,
                showPatientSeenModal: false,
                isPrescriptionModalOpen: false,
                showRequestReportModal: false,
                reportNewRequestDateTime: null,
                isReportUploadedModal: false,
                followUpReferral: false,
            }
        },
        provide() {
            return {
                isReferralPool: false,
            }
        },
        computed: {
            referralTypeClass() {
                return (sourceType) => ({
                    referrals: true,
                    blue: sourceType === ReferralTrackingTypeEnum.DIRECT,
                    purple: sourceType === ReferralTrackingTypeEnum.MATCH,
                })
            },
            activeReferralTab() {
                return this.$route.params.type || ReferralPathTypeEnum.RECEIVED
            },

            handleRescheduleAppointment() {
                return this.followUpReferral ? this.handleFollowUpVisit : this.rescheduleAppointment
            },
            isReferringPhysician() {
                return this.activeReferralTab === ReferralPathTypeEnum.MADE
            },
            receivedPendingCount() {
                if (this.$auth.isUserTemporaryPhysician()) {
                    return 1
                }

                const referrals =
                    Store.getters['auth/getPendingReferralAction'].received?.filter(
                        (status) =>
                            status.referralStatus === ReferralStatusEnum.CREATED ||
                            status.referralStatus === ReferralStatusEnum.PATIENT_VISIT_PAST ||
                            status.referralStatus === ReferralStatusEnum.REFERRALS_POOL_RECEIVED ||
                            status.referralStatus === ReferralStatusEnum.REFERRALS_POOL_RECEIVED_BY_PATIENT,
                    ) || []

                return referrals.reduce((acc, curr) => acc + (Number(curr.count) || 0), 0)
            },

            madePendingCount() {
                const referrals =
                    Store.getters['auth/getPendingReferralAction'].made?.filter(
                        (status) => status.referralStatus === ReferralStatusEnum.REFERRALS_POOL_SLOTS_PROPOSED,
                    ) || []

                return referrals.reduce((acc, curr) => acc + (Number(curr.count) || 0), 0)
            },
        },
        methods: {
            getReferralTabClass(type) {
                return [
                    'referral-tracking__referral-tab',
                    {
                        active: this.activeReferralTab === type,
                    },
                ]
            },
            getReferralCountClass(count) {
                return [
                    'referral-tracking__tab-badge',
                    {
                        'referral-tracking__tab-badge--empty': !count,
                    },
                ]
            },
            toggleDropdown(index) {
                this.activeDropdown = this.activeDropdown === index ? null : index
            },

            closeDropdown() {
                this.activeDropdown = null
            },

            openDetailsModal(id, sourceType, section = null) {
                this.$router.push({
                    name: `referrals.${this.activeReferralTab}`,
                    params: {
                        type: this.activeReferralTab,
                        sourceType: this.mapSourceTypeToUrl(sourceType),
                        id: id,
                        section,
                    },
                })
            },
            closeDetailsModal(skipNavigation = false) {
                this.showViewDetailsModal = false
                this.activeReferral = {}
                this.activeSourceType = null

                if (!skipNavigation) {
                    this.$router.push({
                        name: `referrals.${this.activeReferralTab}`,
                        params: {
                            type: this.activeReferralTab,
                        },
                    })
                }
            },
            mapSourceTypeToUrl(type) {
                if (type === ReferralTrackingTypeEnum.DIRECT) return 'direct'
                if (type === ReferralTrackingTypeEnum.MATCH) return 'match'
                return type
            },
            mapSourceTypeFromUrl(type) {
                if (type === 'direct') return ReferralTrackingTypeEnum.DIRECT
                if (type === 'match') return ReferralTrackingTypeEnum.MATCH
                return type
            },
            openDirectReferralFromMatchReferal(referralId) {
                this.showViewDetailsModal = false

                this.$nextTick(() => {
                    this.openDetailsModal(referralId, 'referral')
                })
            },

            getDropdownPosition() {
                return {
                    position: 'absolute',
                    top: '80%',
                    right: '8px',
                    marginTop: '4px',
                    height: 'auto',
                    zIndex: 1000,
                }
            },

            getCreatedDate(referral) {
                const dateValue = referral?.createdDateTime || referral?.createdAt

                if (!dateValue) return ''

                return new Date(dateValue).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                })
            },

            getUserFullName(patient) {
                return patient ? `${patient.firstName} ${patient.lastName}` : 'N/A'
            },
            getPatientDateOfBirth(dob) {
                if (!dob) return 'N/A'

                const [year, month, day] = dob.split('-')
                return `${month}-${day}-${year}`
            },
            getUserName(referral) {
                if (!referral) return 'N/A'

                const practiceType = this.$auth.getPractice()?.practiceType
                const isMade = this.activeReferralTab === ReferralPathTypeEnum.MADE

                const user = isMade ? referral?.toUser : referral?.fromUser
                const practice = isMade
                    ? referral?.practice || referral?.toPractice || referral?.fromPractice
                    : referral?.fromPractice

                if (practiceType === PracticeTypeEnum.PHYSICIAN && user) {
                    const designation = user.designation ? user.designation + ' ' : ''
                    const firstName = user.firstName || ''
                    const lastName = user.lastName || ''

                    const fullName = `${designation}${firstName} ${lastName}`.trim()
                    if (fullName) return fullName
                }

                if (practiceType === PracticeTypeEnum.ANCILLARY && practice?.name) {
                    return practice.name
                }

                if (practice?.name) return practice.name

                return 'N/A'
            },
            getUserSpeciality(user) {
                if (!user || !user.specialities || user.specialities.length === 0) {
                    return ''
                }

                return user.specialities[0]?.speciality?.name || ''
            },
            getReferralType(type) {
                if (type === ReferralTrackingTypeEnum.DIRECT) return this.$t('pages.referrals.index.referrals_made.type_direct')
                if (type === ReferralTrackingTypeEnum.MATCH) return this.$t('pages.referrals.index.referrals_made.type_matched')
                return 'N/A'
            },
            getStageClass(stage) {
                if (!stage) return 'referral-tracking__referrals gray'

                switch (stage) {
                    case StagesEnum.CONFIRMED:
                    case StagesEnum.AWAITING_PATIENT_VISIT:
                        return 'referral-tracking__referrals green'

                    case StagesEnum.REFERRAL_SUBMITTED:
                    case StagesEnum.AWAITING_PROVIDER_PROPOSALS:
                    case StagesEnum.AWAITING_REFERRER_SELECTION:
                    case StagesEnum.PENDING_PROVIDER_CONFIRMATION:
                        return 'referral-tracking__referrals yellow'

                    case StagesEnum.PROPOSALS_CLOSED:
                        return 'referral-tracking__referrals purple'

                    case StagesEnum.SELECT_A_TIME_SLOT:
                    case StagesEnum.PROPOSALS_RECEIVED:
                        return 'referral-tracking__referrals blue'

                    case StagesEnum.CANCELLED_NO_PROPOSALS:
                    case StagesEnum.CANCELLED_NO_SELECTION:
                        return 'referral-tracking__referrals today'

                    case StagesEnum.PROPOSE_TIME_SLOTS:
                        return 'referral-tracking__referrals purple'

                    default:
                        return 'referral-tracking__referrals gray'
                }
            },
            formatStage(stage) {
                if (!stage) return 'N/A'

                return stage
                    .toLowerCase()
                    .split('_')
                    .map((word) => (word.length > 2 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
                    .join(' ')
            },
            getStageTooltip(referral) {
                return referral?.stageInfo?.nextAction
            },
            isStageClickable(referral) {
                const stage = referral?.stageInfo?.stage
                const isPatientReferralRoute = this.$route.path.includes('/referrals-patient')

                if (
                    (this.activeReferralTab === ReferralPathTypeEnum.RECEIVED || isPatientReferralRoute) &&
                    stage === StagesEnum.PROPOSE_TIME_SLOTS
                ) {
                    return true
                }

                if (
                    this.activeReferralTab === ReferralPathTypeEnum.MADE &&
                    (stage === StagesEnum.SELECT_A_TIME_SLOT || stage === StagesEnum.PROPOSALS_RECEIVED)
                ) {
                    return true
                }

                return false
            },
            handleStageClick(referral) {
                this.selectedReferralForActions = referral
                const stage = referral?.stageInfo?.stage
                const isPatientReferralRoute = this.$route.path.includes('/referrals-patient')
                if (this.activeReferralTab === ReferralPathTypeEnum.RECEIVED || isPatientReferralRoute) {
                    switch (stage) {
                        case StagesEnum.PROPOSE_TIME_SLOTS:
                            this.goToProposeTime(referral, this.activeReferralTab)
                    }
                } else if (this.activeReferralTab === ReferralPathTypeEnum.MADE) {
                    switch (stage) {
                        case StagesEnum.SELECT_A_TIME_SLOT:
                        case StagesEnum.PROPOSALS_RECEIVED:
                            this.chooseSlotMethod({
                                ...referral,
                                referralId: referral.id,
                            })
                    }
                }
            },
            getStageClasses(referral) {
                return [
                    this.getStageClass(referral?.stageInfo?.stage),
                    {
                        'referral-tracking__referrals-clickable': this.isStageClickable(referral),
                    },
                ]
            },

            handleStageAction(referral) {
                if (this.isStageClickable(referral)) {
                    this.handleStageClick(referral)
                }
            },
            getTimingText(referral) {
                const stageInfo = referral?.stageInfo
                if (!stageInfo) return 'N/A'
                if (referral?.sourceType === ReferralTrackingTypeEnum.MATCH) {
                    if (
                        referral.status === ReferralStatusEnum.SCHEDULED ||
                        referral.status === ReferralStatusEnum.DECLINED ||
                        referral.status === ReferralStatusEnum.INACTIVE
                    )
                        return 'N/A'
                    else {
                        const totalHours = stageInfo.hoursLeft

                        if (totalHours <= 0) {
                            return 'N/A'
                        }

                        if (totalHours < 1) {
                            const minutes = Math.ceil(totalHours * 60)
                            return `${minutes} minute${minutes !== 1 ? 's' : ''} left`
                        }

                        const wholeHours = Math.floor(totalHours)
                        const days = Math.floor(wholeHours / 24)
                        const hours = wholeHours % 24

                        if (days > 0 && hours > 0) {
                            return `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours > 1 ? 's' : ''} left`
                        }

                        if (days > 0) {
                            return `${days} day${days > 1 ? 's' : ''} left`
                        }

                        return `${hours} hour${hours > 1 ? 's' : ''} left`
                    }
                } else if (referral?.sourceType === ReferralTrackingTypeEnum.DIRECT) {
                    return 'N/A'
                }
            },

            getTimingClass(stageInfo) {
                if (!stageInfo) return 'referral-tracking__referrals gray'

                const { urgency, hoursLeft } = stageInfo

                const isThreeHoursLeft = hoursLeft >= 0 && hoursLeft <= 3

                return {
                    'referral-tracking__referrals': true,

                    gray: hoursLeft <= 0,

                    green: urgency === UrgencyEnum.LOW && hoursLeft > 3,

                    yellow: urgency === UrgencyEnum.MEDIUM && hoursLeft > 3,

                    today: isThreeHoursLeft || ([UrgencyEnum.HIGH, UrgencyEnum.CRITICAL].includes(urgency) && hoursLeft > 3),
                }
            },
            toggleReferralActiveTab(tab) {
                if (tab === this.activeReferralTab) return

                this.offset = 0
                this.referrals = []

                this.$router.push({ name: `referrals.${tab}` })
            },
            loadReferralsV2(type = this.activeReferralTab, append = false) {
                this.isProcessing = true
                this.referralsTotalCount = 0
                let query
                if (this.$auth.getPractice().practiceType === PracticeTypeEnum.PHYSICIAN) {
                    const userId = this.$auth.getActiveMember()?.id || this.$auth.getUser().id
                    query = {
                        limit: this.limit,
                        offset: this.offset,
                        type: type,
                        userId: userId,
                        ...this.$route.query,
                    }
                } else {
                    const practiceId = this.$auth.getPractice()?.id
                    query = {
                        limit: this.limit,
                        offset: this.offset,
                        type: type,
                        practiceId,
                        ...this.$route.query,
                    }
                }
                ReferralsAPI.getListV2(query).then((res) => {
                    const total = res._headers?.['x-total-count']

                    if (!append) {
                        this.referrals = res
                    } else {
                        this.referrals = [...this.referrals, ...res]
                    }

                    this.referralsTotalCount = Number(total || 0)
                    this.isProcessing = false
                })
            },
            openProvidersModal(id) {
                this.activeReferralPoolId = id
                this.showReferralsPoolProvidersModal = true
            },

            closeProvidersModal() {
                this.activeReferralPoolId = null
                this.showReferralsPoolProvidersModal = false
            },

            showMore() {
                if (this.referrals.length >= this.referralsTotalCount) return

                this.offset += this.limit
                this.loadReferralsV2(this.activeReferralTab, true)
            },

            openRescheduleModal(referral) {
                this.selectedReferralForActions = referral
                this.showRescheduleModal = true
            },

            closeRescheduleModal() {
                this.selectedReferralForActions = null
                this.showRescheduleModal = false
            },

            openCancellationModal() {
                this.showCancellationModal = true
            },

            closeCancellationModal() {
                this.showCancellationModal = false
            },

            openFollowUpVisitModal(referral) {
                this.selectedReferralForActions = referral
                this.showRescheduleModal = true
                this.followUpReferral = true
            },

            closeFollowUpVisitModal() {
                this.selectedReferralForActions = null
                this.showFollowUpVisitModal = false
                this.followUpReferral = false
            },
            handleFollowUpVisit() {
                ReferralsAPI.get(
                    this.selectedReferralForActions.id,
                    this.$auth.getActiveMember()?.id ? this.$auth.getActiveMember()?.id : this.$auth.getUser().id,
                ).then((res) => {
                    const query = {
                        userId: res.fromUser.id,
                        practiceType: res.practice.practiceType,
                        searchBy: 'zipcode',
                        insurancePlanId: res.insurancePlan.id,
                        zip: res.practiceLocation.zip,
                        physicianId: res.toUser.id,
                        practiceId: res.practice.id,
                        practiceLocationId: 0,
                        patientId: res.patient.id,
                        referralId: res.id,
                        followUpVisit: true,
                    }
                    this.$router.push({
                        name: `referrals.new.make`,
                        query: query,
                    })
                })
            },
            rescheduleAppointment() {
                this.reschedule(this.selectedReferralForActions)
            },
            cancelAppointment() {
                return ReferralsAPI.cancel(
                    this.$auth.isUserPracticeOther(),
                    this.selectedReferralForActions.id,
                    this.$auth.getActiveMember() && this.$auth.getActiveMember().id
                        ? this.$auth.getActiveMember().id
                        : this.$auth.getUser().id,
                ).then(() => this.closeCancellationModal())
            },
            cancellationModalDenyAction() {
                this.closeCancellationModal()
                if (this.isReferringPhysician) {
                    this.matchReferral(this.selectedReferralForActions)
                } else {
                    this.openRescheduleModal()
                }
            },
            matchReferral(referral) {
                this.goToMatchSearchPage(referral, this.$auth.getActiveMember().id)
            },
            openPatientSeenModal(referral) {
                this.selectedReferralForActions = referral
                this.showPatientSeenModal = true
            },
            patientSeen(event) {
                if (this.$auth.isUserPracticeDemo()) {
                    return
                }
                ReferralsAPI.updatePatientVisitStatus(this.selectedReferralForActions.id, event.status).then(() => {
                    this.showPatientSeenModal = false
                    this.loadReferralsV2(this.activeReferralTab)
                    if (event.status === 'patient-seen') {
                        this.$router.push({
                            name: `referrals.received.add-report`,
                            params: {
                                id: this.selectedReferralForActions.id,
                            },
                        })
                    }
                })
            },
            addNotesClicked(referral) {
                this.selectedReferralForActions = referral
                this.isReportUploadedModal = true
            },
            downloadPdf(referral) {
                ReferralsAPI.get(
                    referral.id,
                    this.$auth.getActiveMember()?.id ? this.$auth.getActiveMember()?.id : this.$auth.getUser().id,
                ).then((res) => {
                    this.selectedReferralForActions = res
                    this.isPrescriptionModalOpen = true
                })
            },
            closePrescriptionModal() {
                this.selectedReferralForActions = null
                this.isPrescriptionModalOpen = false
            },
            openRequestReportModal(referral) {
                this.selectedReferralForActions = referral
                this.showRequestReportModal = true
            },

            closeRequestReportModal() {
                this.selectedReferralForActions = null
                this.showRequestReportModal = false
            },
            requestReportSent() {
                this.reportNewRequestDateTime = dayjs().format()
                this.$refs.commonSuccessNotificationChild.showNotification()
                this.closeRequestReportModal()
            },
            chooseSlotMethod(data) {
                this.$router.push({
                    name: `referrals.${this.activeReferralTab}.pool.show`,
                    params: {
                        id: data.referralId,
                    },
                })
            },
            handleDetailsModalFromRoute() {
                this.isFetching = true
                const { id, sourceType } = this.$route.params
                if (!id || !sourceType) {
                    this.showViewDetailsModal = false
                    return
                }
                this.showViewDetailsModal = true
                const actualSourceType = this.mapSourceTypeFromUrl(sourceType)
                if (this.activeReferral?.id === id && this.activeSourceType === actualSourceType) return
                this.activeSourceType = actualSourceType
                if (actualSourceType === ReferralTrackingTypeEnum.DIRECT) {
                    ReferralsAPI.get(id, this.$auth.getActiveMember()?.id || this.$auth.getUser().id)
                        .then((res) => {
                            this.activeReferral = res
                        })
                        .finally(() => (this.isFetching = false))
                } else {
                    if (this.$auth.getPractice().practiceType === PracticeTypeEnum.PHYSICIAN) {
                        ReferralsPoolAPI.getReferralPoolById(id, this.activeReferralTab, this.$auth.getActiveMember()?.id)
                            .then((res) => {
                                this.activeReferral = res
                            })
                            .finally(() => (this.isFetching = false))
                    } else {
                        ReferralsPoolAPI.getReferralPoolById(id, this.activeReferralTab, null, this.$auth.getPractice()?.id)
                            .then((res) => {
                                this.activeReferral = res
                            })
                            .finally(() => (this.isFetching = false))
                    }
                }
            },
            updateReferralStatus(referralId) {
                const index = this.referrals.findIndex((r) => r.id === referralId)

                if (index === -1) return

                const userId = this.$auth.getActiveMember()?.id || this.$auth.getUser().id
                const existingSourceType = this.referrals[index]?.sourceType
                ReferralsAPI.get(referralId, userId).then((referral) => {
                    referral.sourceType = existingSourceType
                    this.$set(this.referrals, index, referral)
                })
            },
            refreshMatchReferral(referralId) {
                const index = this.referrals.findIndex((r) => r.id === referralId)

                if (index === -1) return

                const existingSourceType = this.referrals[index]?.sourceType

                const request =
                    this.$auth.getPractice().practiceType === PracticeTypeEnum.PHYSICIAN
                        ? ReferralsPoolAPI.getReferralPoolById(
                              referralId,
                              this.activeReferralTab,
                              this.$auth.getActiveMember()?.id,
                          )
                        : ReferralsPoolAPI.getReferralPoolById(
                              referralId,
                              this.activeReferralTab,
                              null,
                              this.$auth.getPractice()?.id,
                          )

                request.then((res) => {
                    res.sourceType = existingSourceType
                    this.$set(this.referrals, index, res)
                })
            },
            declineReferral(referral) {
                this.isProcessing = true
                ReferralsPoolAPI.decline(referral.id, {
                    referralPoolId: referral.id,
                    userId: this.$auth.getActiveMember()?.id || this.$auth.getUser().id,
                    practiceId: this.$auth.getPractice()?.id,
                })
                    .then(() => {
                        this.refreshMatchReferral(referral.id)
                    })
                    .finally(() => {
                        this.isProcessing = false
                    })
                    .finally(() => {
                        this.activeStatusDropdown = null
                    })
            },
            getStatusText(referral) {
                const displayStatus = this.getDisplayStatus(referral?.status, referral?.sourceType)

                return this.$t(`pages.referrals.tracking.status_title.${displayStatus}`)
            },
            scheduledForDate(date) {
                if (!date) return ''
                return new Date(date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                })
            },
            getDisplayStatus(status, sourceType) {
                const statusMapping = {
                    [ReferralTrackingTypeEnum.MATCH]: {
                        [ReferralStatusEnum.CREATED]: 'CREATED',
                        [ReferralStatusEnum.TIME_SLOTS_PROPOSED]: 'AWAITING_CONFIRMATION',
                        [ReferralStatusEnum.DECLINED]: 'DECLINED',
                        [ReferralStatusEnum.SCHEDULED]: 'SCHEDULED',
                        [ReferralStatusEnum.INACTIVE]: 'EXPIRED',
                    },

                    [ReferralTrackingTypeEnum.DIRECT]: {
                        [ReferralStatusEnum.PATIENT_VISIT_PENDING]: 'SCHEDULED',
                        [ReferralStatusEnum.PATIENT_VISIT_PAST]: 'NO_SHOW',
                        [ReferralStatusEnum.NO_SHOW]: 'NO_SHOW',
                        [ReferralStatusEnum.CANCELLED]: 'CANCELLED',
                        [ReferralStatusEnum.CONSULTATION_NOTES_PENDING]: 'CONSULTATION_NOTES_PENDING',
                        [ReferralStatusEnum.COMPLETED]: 'COMPLETED',
                    },
                }

                return statusMapping[sourceType]?.[status] || status
            },
            getStatusColor(referral) {
                const displayStatus = this.getDisplayStatus(referral?.status, referral?.sourceType)

                const yellowStatuses = ['CREATED', 'AWAITING_CONFIRMATION']

                const blueStatuses = ['SCHEDULED', 'COMPLETED']

                const redStatuses = ['DECLINED', 'CANCELLED', 'NO_SHOW', 'EXPIRED']

                const greenStatuses = ['CONSULTATION_NOTES_PENDING']

                if (yellowStatuses.includes(displayStatus)) return 'yellow'
                if (blueStatuses.includes(displayStatus)) return 'blue'
                if (redStatuses.includes(displayStatus)) return 'today'
                if (greenStatuses.includes(displayStatus)) return 'green'

                return 'gray'
            },
            getStatusBadgeClass(referral) {
                if (!referral) {
                    return 'referral-tracking__referrals gray'
                }

                return {
                    'referral-tracking__referrals': true,
                    'referral-tracking__status-badge': true,
                    [this.getStatusColor(referral)]: true,
                }
            },
            isStatusChangeable(referral) {
                const isDirectReferral = referral?.sourceType === ReferralTrackingTypeEnum.DIRECT

                if (!isDirectReferral) {
                    return false
                }

                if (this.activeReferralTab === ReferralPathTypeEnum.MADE) {
                    return referral.status === ReferralStatusEnum.CREATED
                }

                return [
                    ReferralStatusEnum.NO_SHOW,
                    ReferralStatusEnum.CANCELLED,
                    ReferralStatusEnum.CONSULTATION_NOTES_PENDING,
                ].includes(referral.status)
            },
            handleStatusClick(referral) {
                if (this.activeStatusDropdown === referral.id) {
                    this.activeStatusDropdown = null
                } else {
                    this.activeStatusDropdown = referral.id
                }
            },
            closeStatusDropdown() {
                this.activeStatusDropdown = null
            },
            dropdownOptions(referral) {
                const { status } = referral

                if (this.activeReferralTab === ReferralPathTypeEnum.MADE) {
                    if (status === ReferralStatusEnum.CREATED) {
                        return [ReferralStatusEnum.CANCELLED]
                    }

                    return []
                }

                const optionsMap = {
                    [ReferralStatusEnum.NO_SHOW]: [ReferralStatusEnum.CANCELLED, ReferralStatusEnum.CONSULTATION_NOTES_PENDING],

                    [ReferralStatusEnum.CANCELLED]: [ReferralStatusEnum.NO_SHOW, ReferralStatusEnum.CONSULTATION_NOTES_PENDING],

                    [ReferralStatusEnum.CONSULTATION_NOTES_PENDING]: [ReferralStatusEnum.NO_SHOW, ReferralStatusEnum.CANCELLED],
                }

                return optionsMap[status] || []
            },
            getApiActionForStatus(referralId, selectedStatus) {
                switch (selectedStatus) {
                    case ReferralStatusEnum.NO_SHOW:
                        return {
                            api: ReferralsAPI.updatePatientVisitStatus,
                            args: [referralId, 'no-show'],
                        }

                    case ReferralStatusEnum.CONSULTATION_NOTES_PENDING:
                        return {
                            api: ReferralsAPI.updatePatientVisitStatus,
                            args: [referralId, 'patient-seen'],
                        }

                    case ReferralStatusEnum.CANCELLED:
                        return {
                            api: ReferralsAPI.cancel,
                            args: [
                                this.$auth.isUserPracticeOther(),
                                referralId,
                                this.$auth.getActiveMember() && this.$auth.getActiveMember().id
                                    ? this.$auth.getActiveMember().id
                                    : this.$auth.getUser().id,
                            ],
                        }

                    case ReferralStatusEnum.PATIENT_VISIT_PENDING:
                        return {
                            api: ReferralsAPI.accept,
                            args: [referralId],
                        }

                    default:
                        return null
                }
            },
            handleStatusChange(referral, selectedStatus) {
                const action = this.getApiActionForStatus(referral.id, selectedStatus)
                if (!action) {
                    return
                }
                const { api, args } = action

                api(...args)
                    .then(() => {
                        this.updateReferralStatus(referral.id)
                    })
                    .finally(() => {
                        this.activeStatusDropdown = null
                    })
            },
            getDropdownStatusColor(status, sourceType = ReferralTrackingTypeEnum.DIRECT) {
                const displayStatus = this.getDisplayStatus(status, sourceType)

                if (['NO_SHOW', 'CANCELLED'].includes(displayStatus)) {
                    return 'red'
                }

                if (['CONSULTATION_NOTES_PENDING'].includes(displayStatus)) {
                    return 'green'
                }

                if (['SCHEDULED', 'COMPLETED'].includes(displayStatus)) {
                    return 'blue'
                }

                return 'gray'
            },
            getTimeframeLabel(referral) {
                const timeframe = referral?.timeframe

                const label = TimeWindowSearchCriteriaEnum.find((item) => item.value === timeframe)?.label

                return label || referral?.customDate || timeframe
            },
            hasAdditionalTests(referral) {
                return (
                    referral?.stageInfo?.targetPracticeType === PracticeTypeEnum.ANCILLARY &&
                    (referral?.facilityServiceTests?.length || 0) > 1
                )
            },
            getMatchReferralTitle(referral) {
                if (referral?.stageInfo?.targetPracticeType === PracticeTypeEnum.ANCILLARY) {
                    return referral?.facilityServiceTests?.[0]?.name || '-'
                }

                return referral?.speciality?.name || '-'
            },

            getAdditionalTestsCount(referral) {
                const count = referral?.facilityServiceTests?.length || 0

                return count > 1 ? count - 1 : 0
            },
            getAncillaryTestsTooltip(referral) {
                return referral?.facilityServiceTests?.map((test) => test.name)?.join(', ')
            },
            getProviderCount(referral) {
                return referral?.stageInfo?.totalProviders?.length || 0
            },
            getProviderLabel(referral) {
                const count = this.getProviderCount(referral)

                return count <= 1
                    ? this.$t('pages.referrals.index.referrals_made.view_provider')
                    : this.$t('pages.referrals.index.referrals_made.view_providers', { n: count })
            },
            getReferralMadeMatchIcon(referral) {
                return referral?.stageInfo.targetPracticeType === PracticeTypeEnum.ANCILLARY ? 'ancillary-tests' : 'aala'
            },
            getReferralAvatarClass(referral) {
                return {
                    'provider-match-avatar': true,
                    'provider-match-avatar--ancillary': referral?.stageInfo?.targetPracticeType === PracticeTypeEnum.ANCILLARY,
                }
            },
        },
        watch: {
            '$route.params.type': {
                immediate: true,
                handler(newType, oldType) {
                    if (newType === oldType) return

                    this.offset = 0
                    this.referrals = []
                    this.loadReferralsV2(newType || 'received')
                },
            },
            '$route.params.id': {
                immediate: true,
                handler() {
                    this.handleDetailsModalFromRoute()
                },
            },
        },
    }
</script>
